import { useState, useEffect, useCallback } from 'react'
import { Bookmark, RefreshCw, Trash2, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function BookmarksPage() {
  const [bookmarks, setBookmarks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/content/bookmarks')
      setBookmarks(data.bookmarks || data || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const deleteBookmark = async (id: string) => {
    try {
      await api.delete(`/api/content/bookmarks/${id}`)
      setBookmarks(prev => prev.filter(b => b.id !== id))
    } catch { /* silently fail */ }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Bookmark size={20} className="text-amber" /> Bookmarks
          </h1>
          <p className="text-[10px] text-text-secondary">Saved articles and notes for later</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && bookmarks.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
        </div>
      )}

      {!loading && bookmarks.length === 0 && (
        <div className="card p-8 text-center">
          <Bookmark size={32} className="text-amber/40 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-text-primary mb-1">No bookmarks yet</h3>
          <p className="text-[11px] text-text-secondary max-w-sm mx-auto">
            Bookmark articles from any feed page by clicking the bookmark icon on an article card.
            They will appear here for easy reference.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {bookmarks.map(bm => (
          <div key={bm.id} className="card p-4 hover:border-border-light transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {bm.url ? (
                    <a href={bm.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-semibold text-text-primary hover:text-blue transition-colors truncate flex items-center gap-1">
                      {bm.title || 'Untitled'}
                      <ExternalLink size={10} className="text-text-muted shrink-0" />
                    </a>
                  ) : (
                    <span className="text-sm font-semibold text-text-primary truncate">{bm.title || 'Untitled'}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-text-muted">
                  {bm.source && <span className="bg-surface-raised px-1.5 py-0.5 rounded">{bm.source}</span>}
                  {bm.saved_at && <span>{relativeTime(bm.saved_at)}</span>}
                </div>
                {bm.note && (
                  <p className="text-[11px] text-text-secondary mt-2 bg-surface-raised p-2 rounded border border-border leading-relaxed">
                    {bm.note}
                  </p>
                )}
              </div>
              <button type="button" onClick={() => deleteBookmark(bm.id)}
                className="text-text-muted hover:text-rose transition-colors shrink-0 mt-0.5" title="Delete bookmark">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
